import $ from "jquery";
import { _ } from "extensions/underscore";

import { escapeForCSV } from "common/utils";
import strings from "common/strings";

const TEXT_NODE = 3;

/**
 * Check if provided element is not empty and has cells to export
 * @param index
 * @param {jQuery} row
 * @returns {boolean}
 */
function notEmptyAndHasCells(index, row) {
    return $.css(row, "display") !== "none" && $(row).has("td");
}

/**
 * Examine table rows and find if we need to create a separate column for amount currency
 * @param rows
 * @returns {*} either currency to use or `false`
 */
function getRowCurrency(rows) {
    const firstRow = rows.first();
    const rowCurrencies = firstRow.find(".currency");
    let currency = null;
    
    if (rowCurrencies.length) {
        const firstColumnCurrency = rowCurrencies.first().text();
        const allCurrenciesAreTheSame = _.every(rowCurrencies, (c) => $(c).text() === firstColumnCurrency);
        
        if (allCurrenciesAreTheSame) {
            currency = firstColumnCurrency;
        }
    }
    
    return currency;
}

/**
 * Function takes two DOM TRs and combine them into a single one
 * @param {jQuery} a first row to merge
 * @param {jQuery} b second row to merge
 * @returns {jQuery} combined row
 */
function combineTwoRows(a, b) {
    return $("<tr/>")
        .append(a.children().clone())
        .append(b.children().clone());
}

/**
 * Normalize return with temporary delimiter characters
 * Temporary delimiter characters unlikely to be typed by keyboard
 * This is to avoid accidentally splitting the actual contents
 * @param {jQuery} a an array of strings to normalize
 * @returns {string} normalized CSV string
 * @private
 */
const _normalize = function(a, opts) {
    return a.get()
        .join(opts.tmpRowDelim)
        .split(opts.tmpRowDelim).join(opts.rowDelim)
        .split(opts.tmpColDelim).join(opts.colDelim);
};

/**
 * Generate CSV-like string from provided array of table rows in form of jQuery element
 * @param {jQuery} arr List of matched Table Rows
 * @param {string} cellTag tag of a cell within table row
 * @param {Object} [options] formatting options
 * @param {Array} [options.append = []] items to append to the CSV output
 * @param {Array} [options.prepend = []] items to prepend to the CSV output
 * @param {string} [options.tmpColDelim = String.fromCharCode(11)] temporary column delimiter (Vertical tab character)
 * @param {string} [options.tmpRowDelim = String.fromCharCode(0)] temporary column delimiter (null character)
 * @param {string} [options.colDelim = ","] actual column delimiter
 * @param {string} [options.rowDelim = "\r\n"] actual row delimiter
 * @returns {string}
 */
function getCSVFromArray(arr, cellTag, options) {
    const opts = _.defaults({}, options, {
        append: [],
        prepend: [],
        tmpColDelim: String.fromCharCode(11),
        tmpRowDelim: String.fromCharCode(0),
        colDelim: ",",
        rowDelim: "\r\n",
        alwaysIncludeCurrency: true
    });
    
    // Grab text from table into CSV formatted string
    const _csvData = arr.map((i, row) => {
        const $row = $(row);
        const $cols = $row.find(cellTag);
        
        let columnsArray = $cols.map((j, col) => {
            // Check if the cell has csv data embedded into the DOM object
            // Return embedded data if it does
            const hasEmbeddedCSVData = col.dataset.csvtext;
            if (hasEmbeddedCSVData) {
                return escapeForCSV(hasEmbeddedCSVData);
            }
            
            const $cell = $(col);
            // Returning `null` will exclude this item from the array (jquery's map works that way)
            if ($cell.hasClass("exportExclude")) {
                return null;
            }
            
            const isForceIncludeToExport = $cell.hasClass("exportOnly");
            const cellChildren = $cell.contents().filter((index, elt) => {
                const isHidden = elt.nodeType !== TEXT_NODE && $.css(elt, "display") === "none" && !isForceIncludeToExport;
                const isCurrency = elt.nodeType !== TEXT_NODE && elt.classList.contains("currency");
                
                return !isHidden && (opts.alwaysIncludeCurrency || !isCurrency);
            });
            
            let text;
            if (cellChildren.length) {
                text = cellChildren.text();
            } else {
                text = $cell.text();
            }
            
            return escapeForCSV(text.trim());
        }).get();
        
        if (opts.prepend.length) {
            columnsArray = [...opts.prepend, ...columnsArray];
        }
        
        if (opts.append.length) {
            columnsArray = [...columnsArray, ...opts.append];
        }
        
        return columnsArray.join(opts.tmpColDelim);
    });
    
    return _normalize(_csvData, opts);
}

function getHeadersAndRows(table) {
    const $staticHeaders = table.getChildView("tableStaticHeader").$el;
    const $staticRows = table.ui.tableStaticBody.find("tr").filter(notEmptyAndHasCells);
    let $headers;
    let $rows;
    
    if (table.model.hasLockedColumns()) {
        // In case of a table with locked columns, we need to merge locked part with regular part
        
        const $lockedHeaders = table.getChildView("tableLockedHeader").$el;
        const $lockedRows = table.ui.tableLockedBody.find("tr").filter(notEmptyAndHasCells);
        
        // Create a temporary row with content of both headers
        $headers = combineTwoRows($lockedHeaders, $staticHeaders);
        
        // And populate it with merge of two bodies
        $rows = _.reduce($lockedRows, (memo, cur, index) => memo.add(combineTwoRows($(cur), $staticRows.eq(index))), $());
    } else {
        $headers = $staticHeaders;
        $rows = $staticRows;
    }
    
    return {
        $headers,
        $rows
    };
}

export const toCSV = function(table) {
    const elements = getHeadersAndRows(table);
    const rowCurrency = getRowCurrency(elements.$rows);
    const headerOptions = {};
    const bodyOptions = {};
    
    if (rowCurrency) {
        headerOptions.append = [strings.currency];
        bodyOptions.append = [rowCurrency];
        bodyOptions.alwaysIncludeCurrency = false;
    }
    
    const headerCSV = getCSVFromArray(elements.$headers, "th", headerOptions);
    const bodyCSV = getCSVFromArray(elements.$rows, "td", bodyOptions);
    
    // Glue together header and body
    return `${headerCSV}\r\n${bodyCSV}`;
};
